import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';

interface PaymentFormData {
  defaultCurrency: string;
  depositPercentage: number;
  stripeEnabled: boolean;
  stripePublicKey?: string;
  bankTransferEnabled: boolean;
  bankDetails?: string;
}

interface PaymentSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const currencies = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'TRY', label: 'TRY - Turkish Lira' },
];

const paymentMethods = [
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHECK', label: 'Check' },
];

export function PaymentSettingsModal({ open, onOpenChange }: PaymentSettingsModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enabledCurrencies, setEnabledCurrencies] = useState<string[]>(['USD']);
  const [enabledPaymentMethods, setEnabledPaymentMethods] = useState<string[]>(['CREDIT_CARD', 'BANK_TRANSFER']);

  const { data: settings, isLoading } = trpc.settings.getPaymentSettings.useQuery(undefined, {
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<PaymentFormData>({
    defaultValues: {
      defaultCurrency: 'USD',
      depositPercentage: 30,
      stripeEnabled: false,
      bankTransferEnabled: true,
    },
  });

  const stripeEnabled = watch('stripeEnabled');
  const bankTransferEnabled = watch('bankTransferEnabled');

  useEffect(() => {
    if (settings) {
      reset({
        defaultCurrency: (settings['payment.defaultCurrency'] as string) || 'USD',
        depositPercentage: (settings['payment.depositPercentage'] as number) || 30,
        stripeEnabled: (settings['payment.stripeEnabled'] as boolean) || false,
        stripePublicKey: (settings['payment.stripePublicKey'] as string) || '',
        bankTransferEnabled: (settings['payment.bankTransferEnabled'] as boolean) || true,
        bankDetails: (settings['payment.bankDetails'] as string) || '',
      });
      setEnabledCurrencies((settings['payment.enabledCurrencies'] as string[]) || ['USD']);
      setEnabledPaymentMethods((settings['payment.paymentMethods'] as string[]) || ['CREDIT_CARD', 'BANK_TRANSFER']);
    }
  }, [settings, reset]);

  const utils = trpc.useUtils();

  const updateMutation = trpc.settings.updatePaymentSettings.useMutation({
    onSuccess: () => {
      utils.settings.getPaymentSettings.invalidate();
      onOpenChange(false);
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: PaymentFormData) => {
    setIsSubmitting(true);
    updateMutation.mutate({
      ...data,
      enabledCurrencies,
      paymentMethods: enabledPaymentMethods,
    });
  };

  const toggleCurrency = (currency: string) => {
    setEnabledCurrencies((prev) =>
      prev.includes(currency)
        ? prev.filter((c) => c !== currency)
        : [...prev, currency]
    );
  };

  const togglePaymentMethod = (method: string) => {
    setEnabledPaymentMethods((prev) =>
      prev.includes(method)
        ? prev.filter((m) => m !== method)
        : [...prev, method]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Payment Settings</DialogTitle>
          <DialogDescription>
            Configure payment methods, currencies, and gateway settings.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Currency Settings */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Currency Settings</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Default Currency</Label>
                  <Combobox
                    options={currencies.map((currency) => ({
                      value: currency.value,
                      label: currency.label,
                    }))}
                    value={watch('defaultCurrency')}
                    onValueChange={(value) => setValue('defaultCurrency', value)}
                    placeholder="Select currency"
                    searchPlaceholder="Search currencies..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="depositPercentage">Deposit Percentage *</Label>
                  <Input
                    id="depositPercentage"
                    type="number"
                    min="0"
                    max="100"
                    {...register('depositPercentage', {
                      required: 'Deposit percentage is required',
                      valueAsNumber: true,
                      min: { value: 0, message: 'Must be at least 0%' },
                      max: { value: 100, message: 'Cannot exceed 100%' },
                    })}
                  />
                  {errors.depositPercentage && (
                    <p className="text-sm text-red-500">{errors.depositPercentage.message}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Enabled Currencies</Label>
                <div className="flex flex-wrap gap-2">
                  {currencies.map((currency) => (
                    <button
                      key={currency.value}
                      type="button"
                      onClick={() => toggleCurrency(currency.value)}
                      className={`rounded-full px-3 py-1 text-sm transition-colors ${
                        enabledCurrencies.includes(currency.value)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      {currency.value}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Payment Methods</h3>
              <div className="flex flex-wrap gap-2">
                {paymentMethods.map((method) => (
                  <button
                    key={method.value}
                    type="button"
                    onClick={() => togglePaymentMethod(method.value)}
                    className={`rounded-full px-3 py-1 text-sm transition-colors ${
                      enabledPaymentMethods.includes(method.value)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {method.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stripe Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium">Stripe Integration</h3>
                  <p className="text-sm text-muted-foreground">Accept credit card payments online</p>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    {...register('stripeEnabled')}
                    className="peer sr-only"
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20"></div>
                </label>
              </div>
              {stripeEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="stripePublicKey">Stripe Public Key</Label>
                  <Input
                    id="stripePublicKey"
                    {...register('stripePublicKey')}
                    placeholder="pk_live_..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Your Stripe secret key should be configured in environment variables
                  </p>
                </div>
              )}
            </div>

            {/* Bank Transfer Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium">Bank Transfer</h3>
                  <p className="text-sm text-muted-foreground">Accept bank transfer payments</p>
                </div>
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    {...register('bankTransferEnabled')}
                    className="peer sr-only"
                  />
                  <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20"></div>
                </label>
              </div>
              {bankTransferEnabled && (
                <div className="space-y-2">
                  <Label htmlFor="bankDetails">Bank Account Details</Label>
                  <Textarea
                    id="bankDetails"
                    {...register('bankDetails')}
                    placeholder="Bank Name: ...&#10;Account Number: ...&#10;IBAN: ...&#10;SWIFT: ..."
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    This information will be shown on invoices and payment instructions
                  </p>
                </div>
              )}
            </div>

            {updateMutation.error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {updateMutation.error.message}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
